import React, { useState, useEffect } from 'react'
import { useDonation } from '../context/DonationContext'
import { formatCurrency } from '../utils/formatCurrency'
import { DonorData } from '../types'
import ThankYouModal from './ThankYouModal'
import { sendBulkThankYouEmails } from '../actions/sendBulkThankYou'
import { FaEye, FaEyeSlash, FaEnvelope } from 'react-icons/fa'
import md5 from 'md5'

const StatusIndicator = ({ donor, isSending }: { donor: DonorData, isSending: boolean }) => (
  <span 
    className={`block w-3 h-3 rounded-full cursor-help ${
      isSending
        ? 'border-2 border-yellow-600 border-t-transparent animate-spin'
        : donor.thankYouSent 
          ? 'bg-green-600' 
          : 'bg-red-600'
    }`}
    title={
      isSending
        ? 'Sending thank you...'
        : donor.thankYouSent 
          ? `Thank you sent on ${new Date(donor.thankYouSent.date).toLocaleDateString()}`
          : 'Thank you not sent yet'
    }
  />
)

export default function DonationsByDonor() {
  const { 
    state: { filteredDonors: donors, currentSort, filterStartDate, filterEndDate },
    setCurrentSort,
    markThankYouSent,
    clearSentStatus
  } = useDonation()
  
  const [expandedDonor, setExpandedDonor] = useState<string | null>(null)
  const [selectedDonor, setSelectedDonor] = useState<DonorData | null>(null)
  const [selectedDonors, setSelectedDonors] = useState<Set<string>>(new Set())
  const [isSending, setIsSending] = useState(false)
  const [sendingProgress, setSendingProgress] = useState(0)
  const [sendingDonors, setSendingDonors] = useState<Set<string>>(new Set())

  const toggleSort = (column: string) => {
    if (currentSort.column === column) {
      setCurrentSort({ ...currentSort, order: currentSort.order === 'asc' ? 'desc' : 'asc' })
    } else {
      setCurrentSort({ column, order: 'asc' })
    }
  }

  const getSortIcon = (column: string) => {
    if (currentSort.column === column) {
      return currentSort.order === 'asc' ? '▲' : '▼'
    }
    return ''
  }

  const toggleDonationDetails = (id: string) => {
    setExpandedDonor(expandedDonor === id ? null : id)
  }

  const toggleDonorSelection = (id: string) => {
    const newSelected = new Set(selectedDonors)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedDonors(newSelected)
  }

  const selectUnsent = () => {
    const unsentDonors = donorsList
      .filter(d => !d.thankYouSent)
      .map(d => d.id)
    setSelectedDonors(new Set(unsentDonors))
  }

  // Convert donors object to array for display
  const donorsList = Object.values(donors)

  const sortedDonors = [...donorsList].sort((a: DonorData, b: DonorData) => {
    let valueA = a[currentSort.column as keyof DonorData]
    let valueB = b[currentSort.column as keyof DonorData]

    // Handle date sorting
    if (currentSort.column === 'lastDonationDate') {
      valueA = new Date(valueA as string).getTime()
      valueB = new Date(valueB as string).getTime()
    }
    // Handle numeric sorting
    else if (typeof valueA === 'number' && typeof valueB === 'number') {
      // Keep as is
    }
    // Handle string sorting
    else {
      valueA = valueA?.toString().toLowerCase() ?? ''
      valueB = valueB?.toString().toLowerCase() ?? ''
    }

    if (valueA < valueB) return currentSort.order === 'asc' ? -1 : 1
    if (valueA > valueB) return currentSort.order === 'asc' ? 1 : -1
    return 0
  })

  useEffect(() => {
    console.log('Current donors:', sortedDonors)
  }, [sortedDonors])

  const updateSendingState = (donorId: string, isAdding: boolean) => {
    setSendingDonors(prev => {
      const newSet = new Set(prev)
      isAdding ? newSet.add(donorId) : newSet.delete(donorId)
      return newSet
    })
  }

  const handleBulkThankYou = async () => {
    const selectedDonorsList = sortedDonors.filter(d => selectedDonors.has(d.id))
    setIsSending(true)
    setSendingProgress(0)

    try {
      const donorsToProcess = selectedDonorsList.map(donor => ({
        name: donor.name,
        email: donor.email,
        total: donor.total,
        dateRange: {
          start: filterStartDate.toISOString(),
          end: filterEndDate.toISOString()
        },
        donations: donor.donations
      }))

      const result = await sendBulkThankYouEmails(donorsToProcess)
      
      if (result.success && result.results) {
        for (const r of result.results) {
          if (r.success) {
            markThankYouSent(r.email, r.name, {
              start: filterStartDate.toISOString(),
              end: filterEndDate.toISOString()
            })
          }
          const donorId = md5(r.email.toLowerCase() + r.name.toLowerCase())
          updateSendingState(donorId, false)
        }
      }

      setSendingProgress(100)
    } catch (error) {
      console.error('Error sending thank you emails:', error)
    } finally {
      setIsSending(false)
      setSendingProgress(0)
      setSelectedDonors(new Set())
    }
  }

  return (
    <>
      <div className="mb-4 flex gap-2 items-center">
        <button 
          onClick={clearSentStatus}
          className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded"
        >
          Clear Sent Status
        </button>
        <button 
          onClick={selectUnsent}
          className="bg-secondary hover:bg-secondary/80 text-white px-4 py-2 rounded"
        >
          Select Unsent
        </button>
        {selectedDonors.size > 0 && (
          <button 
            onClick={handleBulkThankYou}
            disabled={isSending}
            className={`bg-primary hover:bg-primary/80 text-white px-4 py-2 rounded flex items-center gap-2 ${
              isSending ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isSending ? (
              <>
                <span className="animate-spin">⏳</span>
                Sending... ({Math.round(sendingProgress)}%)
              </>
            ) : (
              `Send Thank You (${selectedDonors.size})`
            )}
          </button>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <input 
                  type="checkbox"
                  checked={selectedDonors.size > 0}
                  onChange={() => setSelectedDonors(
                    selectedDonors.size > 0
                      ? new Set() 
                      : new Set(sortedDonors.map(d => d.id))
                  )}
                  className="ml-5 w-4 h-4 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary focus:ring-2"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('name')}>
                Name {getSortIcon('name')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('email')}>
                Email {getSortIcon('email')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('country')}>
                Country {getSortIcon('country')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('total')}>
                Total Amount {getSortIcon('total')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('count')}>
                Number of Donations {getSortIcon('count')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('lastDonationDate')}>
                Last Donation Date {getSortIcon('lastDonationDate')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedDonors.map((donor, index) => (
              <React.Fragment key={donor.id}>
                <tr className={`
                  ${sendingDonors.has(donor.id)
                    ? 'bg-yellow-50'
                    : donor.thankYouSent
                      ? 'bg-green-50'
                      : 'bg-red-50'
                  }
                  hover:bg-opacity-80 transition-colors
                `}>
                  <td className="px-6 py-4 whitespace-nowrap flex items-center gap-2">
                    <StatusIndicator 
                      donor={donor} 
                      isSending={sendingDonors.has(donor.id)} 
                    />
                    <input 
                      type="checkbox"
                      checked={selectedDonors.has(donor.id)}
                      onChange={() => toggleDonorSelection(donor.id)}
                      className="w-4 h-4 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary focus:ring-2"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{donor.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{donor.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{donor.country}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{formatCurrency(donor.total)}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{donor.count}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {new Date(donor.lastDonationDate).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button 
                      onClick={() => toggleDonationDetails(donor.id)}
                      className="text-primary hover:text-primary/80"
                      title={expandedDonor === donor.id ? 'Hide Details' : 'Show Details'}
                    >
                      {expandedDonor === donor.id ? <FaEyeSlash /> : <FaEye />}
                    </button>
                    <button 
                      onClick={() => setSelectedDonor(donor)}
                      className="ml-4 text-secondary hover:text-secondary/80"
                      title="Send Thank You"
                    >
                      <FaEnvelope />
                    </button>
                  </td>
                </tr>
                {expandedDonor === donor.id && (
                  <tr>
                    <td colSpan={8}>
                      <div className="px-40 py-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-4">Donation History</h4>
                        <table className="m-auto w-full divide-y divide-gray-200">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                              <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                              <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white">
                            {donor.donations.map((donation, donationIndex) => {
                              const donationDate = new Date(donation['Created at'])
                              return (
                                <tr key={`${donation.Email}-${donation['Created at']}-${donationIndex}`}>
                                  <td className="px-6 py-2 text-sm text-gray-600">{donationDate.toLocaleDateString()}</td>
                                  <td className="px-6 py-2 text-sm text-gray-600">{donationDate.toLocaleTimeString()}</td>
                                  <td className="px-6 py-2 text-sm text-gray-600">{formatCurrency(parseFloat(donation.Amount))}</td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {selectedDonor && (
        <ThankYouModal 
          donor={selectedDonor} 
          isOpen={!!selectedDonor} 
          onClose={() => setSelectedDonor(null)}
          sendingDonors={sendingDonors}
          setSendingDonors={setSendingDonors}
        />
      )}
    </>
  )
}

